import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './features/auth/context';
import { ConsentProvider, ConsentBanner } from './shared/consent';
import { initMixpanel } from './shared/analytics';
import Header from './components/Header';
import Footer from './components/Footer';
import RequireAdmin from './features/auth/RequireAdmin';
import RequireAuth from './features/auth/RequireAuth';
import RequireAuthOrGuest from './features/auth/RequireAuthOrGuest';
import GuestWallHost from './features/auth/GuestWallHost';
import { hasGuestSession, getGuestTripId } from './features/auth/guest';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import FriendsPage from './pages/FriendsPage';
import JourneyComparisonPage from './pages/JourneyComparisonPage';
import MyTripsPage from './pages/MyTripsPage';
import TripFormPage from './pages/TripFormPage';
import TripDetailPage from './pages/TripDetailPage';
import JourneyDetailPage from './pages/JourneyDetailPage';
import UnsortedJourneysPage from './pages/UnsortedJourneysPage';
import AdminPanel from './pages/AdminPanel';
import CarbonAccountingTestPage from './pages/CarbonAccountingTestPage';
import AdminAffiliatePage from './pages/AdminAffiliatePage';
import AdminEmailsPage from './pages/AdminEmailsPage';
import AdminLlmRequestsPage from './pages/AdminLlmRequestsPage';
import RechartsTestPage from './pages/RechartsTestPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import NightTrainMapPage from './pages/NightTrainMapPage';
import { ToastProvider } from './components/ToastProvider';
import SessionExpiryHandler from './features/auth/SessionExpiryHandler';
import TekkrProgramView from './features/tekkr/pages/TekkrProgramView';
import TekkrProjectView from './features/tekkr/pages/TekkrProjectView';
import AdminWaitlistPage from './pages/AdminWaitlistPage';
import TripCalculatorPage from './pages/TripCalculatorPage';
import SharedTripPage from './pages/SharedTripPage';
import SharedJourneyPage from './pages/SharedJourneyPage';

// The root "/" is no longer a landing page — the marketing site lives on a
// separate domain. Signed-in users go to their primary surface (My Trips);
// a guest (anonymous access) returns to their trip; everyone else lands on
// the trip planner — the login-free door (anonymous access v1 §2). The
// planner route is public, so this can't loop with RequireAuth.
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/trips" replace />;
  const guestTripId = hasGuestSession() ? getGuestTripId() : null;
  if (guestTripId) return <Navigate to={`/trips/${encodeURIComponent(guestTripId)}`} replace />;
  return <Navigate to="/trips/new" replace />;
}

function AppContent() {
  const location = useLocation();
  const isTekkriRoute = location.pathname.startsWith('/tekkr');

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {!isTekkriRoute && <Header />}
      <main style={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
                <Route path="/admin/carbon-accounting" element={<RequireAdmin><CarbonAccountingTestPage /></RequireAdmin>} />
                <Route path="/admin/affiliate" element={<RequireAdmin><AdminAffiliatePage /></RequireAdmin>} />
                <Route path="/admin/emails" element={<RequireAdmin><AdminEmailsPage /></RequireAdmin>} />
                <Route path="/admin/llm-requests" element={<RequireAdmin><AdminLlmRequestsPage /></RequireAdmin>} />
                <Route path="/admin/waitlist" element={<RequireAdmin><AdminWaitlistPage /></RequireAdmin>} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/friends" element={<RequireAuth><FriendsPage /></RequireAuth>} />
                {/* LEGACY (being sunset, replaced by My Trips): /journey-comparison
                    has no in-app entry point left and is reachable only by direct
                    URL during the transition. Remove
                    once the My Trips migration is complete — see
                    docs/product/my-trips-backlog.md ("Legacy pages — sunset & removal"). */}
                <Route path="/journey-comparison" element={<RequireAuth><JourneyComparisonPage /></RequireAuth>} />
                <Route path="/trips" element={<RequireAuth><MyTripsPage /></RequireAuth>} />
                {/* Static segments rank above the dynamic ":id" in React Router v6. */}
                {/* /trips/new is the login-free door: fully public, guest
                    session created on submit (anonymous access v1 §2). Trip
                    and journey detail admit guests via RequireAuthOrGuest. */}
                <Route path="/trips/new" element={<TripFormPage />} />
                <Route path="/trips/unsorted" element={<RequireAuth><UnsortedJourneysPage /></RequireAuth>} />
                <Route path="/trips/:id/edit" element={<RequireAuth><TripFormPage /></RequireAuth>} />
                <Route path="/trips/:id" element={<RequireAuthOrGuest sharedFallback="trip"><TripDetailPage /></RequireAuthOrGuest>} />
                <Route path="/journeys/:id" element={<RequireAuthOrGuest sharedFallback="journey"><JourneyDetailPage /></RequireAuthOrGuest>} />
                {/* Trip sharing v1: public read-only capability URLs, no auth
                    guard on purpose (spec §7). Access control is the token. */}
                <Route path="/s/:token" element={<SharedTripPage />} />
                <Route path="/s/:token/journeys/:journeyId" element={<SharedJourneyPage />} />
                <Route path="/recharts-test" element={<RechartsTestPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                {/* The night-train timetables' open-data credits (spec §7.5) live under the
                    Terms since 2026-10-01; the old "Data sources" page sends people there. */}
                <Route path="/data-sources" element={<Navigate to="/terms-and-conditions#open-data" replace />} />
                {/* Every night train on one map (backlog #75); not linked from the app yet. */}
                <Route path="/night-trains" element={<NightTrainMapPage />} />
                <Route path="/tekkr/program-view" element={<TekkrProgramView />} />
                <Route path="/tekkr/project-view" element={<TekkrProjectView />} />
                {/* The pre-launch waitlist page is gone (waitlist sunset,
                    Aug 2026). Old ads/links land on signup instead. */}
                <Route path="/waitlist" element={<Navigate to="/signup" replace />} />
                <Route path="/travel-impact-calculator" element={<TripCalculatorPage />} />
                {/* Backwards-compat: keep the old `/calculator` URL
                    working for anyone with the pre-launch link saved. */}
                <Route
                  path="/calculator"
                  element={<Navigate to="/travel-impact-calculator" replace />}
                />
              </Routes>
            </main>
      {!isTekkriRoute && <Footer />}
          </div>
  );
}

function App() {
  // PILOT-TIME EXCEPTION: Mixpanel is initialised unconditionally
  // during the controlled pilot, under legitimate interest for product
  // analytics. ePrivacy strictly requires opt-in for the localStorage
  // `mp_*` distinct_id this writes, so this is technically non-compliant
  // for EU users and **must be re-gated behind cookie consent before
  // the full public launch of Jody.** Tracked as Item H in the
  // calculator backlog and disclosed in the Privacy Policy (Section 14
  // and Section 19.4). Hotjar remains consent-gated — see
  // `shared/consent/useConsent.tsx`.
  useEffect(() => {
    initMixpanel();
  }, []);

  return (
    <ConsentProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <SessionExpiryHandler />
            <GuestWallHost />
            <AppContent />
            <ConsentBanner />
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ConsentProvider>
  );
}

export default App;
